import { useClientPortalAPI } from "@certn/api";
import { Loader } from "@certn/cascadia-react";
import { EVENT_NAMES, slugify, useEventTracking } from "@certn/utils";
import { css } from "@emotion/css";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useNavigate } from "react-router-dom";

import { PackageCard } from "../package-card/PackageCard";
import { PackageCardLegacy } from "../package-card/PackageCardLegacy";

interface PackageListProps {
  selectedGroup: string | null;
  search?: string;
}

export function PackageList({ selectedGroup, search }: PackageListProps) {
  const navigate = useNavigate();
  const { trackEvent } = useEventTracking();
  const { clientPortalAddingApplyLinksToPackages } = useFlags();

  const queryParams =
    search && search.trim().length > 0 ? { search: search.trim() } : undefined;

  const {
    data: packages,
    isLoading,
    mutate,
  } = useClientPortalAPI(
    selectedGroup
      ? `/api/internal/client-portal/order/${selectedGroup}/packages/`
      : null,
    queryParams
  );

  if (isLoading) {
    return <Loader width="100%" testID="packages-loader" />;
  }

  const searchTerm = search?.trim().toLowerCase() ?? "";
  const isApiSearch = Boolean(queryParams?.search);
  const visiblePackages =
    packages?.filter((packageItem) =>
      isApiSearch || !searchTerm
        ? true
        : packageItem.name.toLowerCase().includes(searchTerm)
    ) ?? [];

  return (
    <div className={styles.packages}>
      {clientPortalAddingApplyLinksToPackages &&
        visiblePackages.map((packageItem) => (
          <PackageCard
            mutate={mutate}
            packageId={packageItem.id}
            selectedGroup={selectedGroup}
            key={packageItem.id}
            title={packageItem.name}
            checks={packageItem.check_details.map((check) => check.name)}
            description={packageItem.description}
            tags={packageItem.tag_names.map((tag) => tag.value)}
            defaultApplyLink={packageItem.default_apply_link}
            onPress={() => {
              trackEvent(EVENT_NAMES.CP_SELECTED_PACKAGE, {
                packageName: packageItem.name,
              });
              navigate(
                `/order?package_id=${packageItem.id}&group_id=${selectedGroup}&step=1`
              );
            }}
            testID={slugify(packageItem.name)}
            isDisabled={!packageItem.can_order}
            canApplyLinkBeCreated={packageItem.can_create_apply_link}
          />
        ))}
      {!clientPortalAddingApplyLinksToPackages &&
        visiblePackages.map((packageItem) => (
          <PackageCardLegacy
            key={packageItem.id}
            title={packageItem.name}
            checks={packageItem.check_details.map((check) => check.name)}
            description={packageItem.description}
            tags={packageItem.tag_names.map((tag) => tag.value)}
            onPress={() => {
              trackEvent(EVENT_NAMES.CP_SELECTED_PACKAGE, {
                packageName: packageItem.name,
              });
              navigate(
                `/order?package_id=${packageItem.id}&group_id=${selectedGroup}&step=1`
              );
            }}
            testID={slugify(packageItem.name)}
            isDisabled={!packageItem.can_order}
          />
        ))}
    </div>
  );
}

const styles = {
  packages: css({
    display: "flex",
    flexWrap: "wrap",
    rowGap: "var(--space-3)",
    columnGap: "var(--space-4)",
    padding: "var(--space-4)",
    backgroundColor: "var(--surface-sunken)",
    minHeight: "192px",
  }),
};
